import moment from 'moment';
import { TimeBox } from '../types/timeBox';
import { timeBoxCategoryConfig } from '../constants/timebox.constants';
import { Event, EventTimeType, EventPreparation, EventSource, EventCategory } from '../types/calendar-event';
import { ScheduleItem } from '../types';
import { TIME_GRID_INTERVAL_MINUTES, snapTimeToGrid } from './timePickerHelpers';

// 生成指定時間範圍的小時數組
export const generateHours = (startHour: number = 0, endHour: number = 23) => {
  return Array.from({ length: endHour - startHour + 1 }, (_, i) => i + startHour);
};

// 計算指定日期範圍內的日期數組
export const generateDateRange = (startDate: Date, days: number) => {
  const dates = [];
  const currentDate = new Date(startDate);
  
  for (let i = 0; i < days; i++) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
};

// 獲取指定日期的時間箱
export const getTimeBoxesForDay = (timeBoxes: TimeBox[], day: Date) => {
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  
  const dayEnd = new Date(day);
  dayEnd.setHours(23, 59, 59, 999);
  
  return timeBoxes.filter(
    timeBox => 
      timeBox.startTime >= dayStart && 
      timeBox.startTime <= dayEnd
  );
};

// 獲取指定日期的事件
export const getEventsForDay = (events: Event[], day: Date) => {
  // 使用更可靠的日期比較方法，確保使用本地時間而不是 UTC 時間
  const targetYear = day.getFullYear();
  const targetMonth = day.getMonth();
  const targetDate = day.getDate();
  
  const dayStart = new Date(targetYear, targetMonth, targetDate, 0, 0, 0, 0);
  const dayEnd = new Date(targetYear, targetMonth, targetDate, 23, 59, 59, 999);
  
  return events.filter(event => {
    // 對於 allday 事件，檢查日期範圍
    if (event.timeType === 'allday') {
      const eventStartDay = new Date(event.startTime);
      eventStartDay.setHours(0, 0, 0, 0);
      
      const eventEndDay = event.endTime ? new Date(event.endTime) : new Date(event.startTime);
      eventEndDay.setHours(23, 59, 59, 999); // 設置為當天結束時間
      
      // 檢查事件日期範圍是否與當天有重疊
      return dayStart <= eventEndDay && dayEnd >= eventStartDay;
    }
    
    // 對於 timed 事件，使用更穩健的日期比較
    // 直接比較年月日，而不是依賴時間戳的邊界比較
    const eventYear = event.startTime.getFullYear();
    const eventMonth = event.startTime.getMonth();
    const eventDate = event.startTime.getDate();
    
    return (
      eventYear === targetYear &&
      eventMonth === targetMonth &&
      eventDate === targetDate
    );
  });
};

// 獲取指定日期的所有日程項目
export const getScheduleItemsForDay = (scheduleItems: ScheduleItem[], day: Date) => {
  // 使用與 getEventsForDay 相同的可靠日期比較方法
  const targetYear = day.getFullYear();
  const targetMonth = day.getMonth();
  const targetDate = day.getDate();
  
  const dayStart = new Date(targetYear, targetMonth, targetDate, 0, 0, 0, 0);
  const dayEnd = new Date(targetYear, targetMonth, targetDate, 23, 59, 59, 999);
  
  return scheduleItems.filter(item => {
    // 對於 Event
    if ('timeType' in item) {
      const event = item as Event;
      if (event.timeType === 'allday') {
        const eventStartDay = new Date(event.startTime);
        eventStartDay.setHours(0, 0, 0, 0);
        
        const eventEndDay = event.endTime ? new Date(event.endTime) : new Date(event.startTime);
        eventEndDay.setHours(23, 59, 59, 999); // 設置為當天結束時間
        
        // 檢查事件日期範圍是否與當天有重疊
        return dayStart <= eventEndDay && dayEnd >= eventStartDay;
      } else {
        // 對於 timed 事件，使用年月日直接比較
        const eventYear = event.startTime.getFullYear();
        const eventMonth = event.startTime.getMonth();
        const eventDate = event.startTime.getDate();
        
        return (
          eventYear === targetYear &&
          eventMonth === targetMonth &&
          eventDate === targetDate
        );
      }
    }
    
    // 對於 TimeBox，也使用年月日直接比較
    const itemYear = item.startTime.getFullYear();
    const itemMonth = item.startTime.getMonth();
    const itemDate = item.startTime.getDate();
    
    return (
      itemYear === targetYear &&
      itemMonth === targetMonth &&
      itemDate === targetDate
    );
  });
};

// 計算時間箱在日曆中的樣式位置
export const calculateTimeBoxPosition = (timeBox: TimeBox, hourHeight: number = 60, startHour: number = 0) => {
  const startTimeHour = timeBox.startTime.getHours();
  const startTimeMinute = timeBox.startTime.getMinutes();
  
  const endTimeHour = timeBox.endTime.getHours();
  const endTimeMinute = timeBox.endTime.getMinutes();
  
  // 計算頂部位置（相對於起始小時）
  const top = ((startTimeHour - startHour) * hourHeight) + ((startTimeMinute / 60) * hourHeight);
  
  // 計算高度（持續時間）
  const durationHours = endTimeHour - startTimeHour + (endTimeMinute - startTimeMinute) / 60;
  const height = durationHours * hourHeight;
  
  return { top, height };
};

// 計算事件在日曆中的樣式位置
export const calculateEventPosition = (event: Event, hourHeight: number = 60, startHour: number = 0) => {
  // 對於全天事件，返回特殊位置
  if (event.timeType === 'allday') {
    return {
      top: 0,
      height: 30, // 全天事件固定高度
      isAllDay: true
    };
  }
  
  const startTimeHour = event.startTime.getHours();
  const startTimeMinute = event.startTime.getMinutes();
  
  // 計算頂部位置（相對於起始小時）
  const top = ((startTimeHour - startHour) * hourHeight) + ((startTimeMinute / 60) * hourHeight);
  
  // 計算高度（持續時間）
  let height = hourHeight; // 默認 1 小時高度
  
  if (event.endTime) {
    const endTimeHour = event.endTime.getHours();
    const endTimeMinute = event.endTime.getMinutes();
    const durationHours = endTimeHour - startTimeHour + (endTimeMinute - startTimeMinute) / 60;
    height = durationHours * hourHeight;
  }
  
  return { top, height, isAllDay: false };
};

// 統一的位置計算函數
export const calculateScheduleItemPosition = (item: ScheduleItem, hourHeight: number = 60, startHour: number = 0) => {
  if ('timeType' in item) {
    return calculateEventPosition(item as Event, hourHeight, startHour);
  } else {
    return { ...calculateTimeBoxPosition(item as TimeBox, hourHeight, startHour), isAllDay: false };
  }
};

// 處理後的日程項目介面
export interface ProcessedScheduleItem {
  id: string;
  title: string;
  startTime: Date;
  endTime?: Date;
  userId: string;
  createdAt: Date;
  description?: string;
  location?: string;
  notes?: string;
  position: number;           // 水平位置索引 (0, 1, 2...)
  totalPositions: number;     // 該時間段總位置數
  itemType: 'timebox' | 'event'; // 保持原有類型標識

  // TimeBox 特有屬性
  category?: string | EventCategory;  // 可以是 TimeBox 的 string 或 Event 的 EventCategory
  taskId?: string;
  updatedAt?: Date;
  source?: EventSource | {     // Event 使用字串，TimeBox 使用物件
    type: string;
    version?: string;
    templateId?: string;
  };

  // Event 特有屬性
  timeType?: EventTimeType;
  preparation?: EventPreparation;
}

/**
 * 分離全天事件和定時項目的工具函數
 * @param scheduleItems 所有日程項目
 * @returns 分離後的全天事件和定時項目
 */
export const separateAllDayAndTimedItems = (scheduleItems: ScheduleItem[]) => {
  const allDayEvents = scheduleItems.filter(item => 
    item.itemType === 'event' && item.timeType === 'allday'
  );
  const timedItems = scheduleItems.filter(item => 
    item.itemType !== 'event' || item.timeType === 'timed'
  );
  return { allDayEvents, timedItems };
};

/**
 * 統一的重疊處理函數
 * 處理 TimeBox 和 Event 的混合重疊，確保完全錯開顯示
 * @param scheduleItems 所有日程項目（TimeBox 和 Event）
 * @param startHour 顯示範圍的開始小時
 * @param endHour 顯示範圍的結束小時
 * @returns 處理後的日程項目，包含位置信息
 */
export const processOverlappingScheduleItems = (
  scheduleItems: ScheduleItem[], 
  startHour: number = 0, 
  endHour: number = 23
): ProcessedScheduleItem[] => {
  if (scheduleItems.length === 0) return [];
  
  // 過濾出在顯示範圍內的項目
  const filteredItems = scheduleItems.filter(item => {
    const startHour_item = item.startTime.getHours();
    const endHour_item = item.endTime ? item.endTime.getHours() : startHour_item;
    
    // 項目必須與顯示時間範圍有交集
    return !(endHour_item < startHour || startHour_item > endHour);
  });
  
  // 按開始時間排序（TimeBox 和 Event 混合排序）
  const sortedItems = [...filteredItems].sort(
    (a, b) => a.startTime.getTime() - b.startTime.getTime()
  );
  
  // 處理重疊檢測和位置分配
  const processed: ProcessedScheduleItem[] = [];
  const timeSlots = new Map<string, number[]>(); // 時間槽 -> 已佔用的位置數組
  
  for (const item of sortedItems) {
    // 計算項目涵蓋的 15 分鐘時間槽
    const startMinutes = item.startTime.getHours() * 60 + item.startTime.getMinutes();
    const endMinutes = item.endTime 
      ? item.endTime.getHours() * 60 + item.endTime.getMinutes()
      : startMinutes + 60; // 默認 1 小時
    
    // 尋找可用的水平位置
    let position = 0;
    let maxPositionsNeeded = 1;
    
    // 檢查每個 15 分鐘時間槽的佔用情況
    for (let minute = startMinutes; minute < endMinutes; minute += 15) {
      const slotKey = Math.floor(minute / 15).toString();
      const occupiedPositions = timeSlots.get(slotKey) || [];
      
      // 找到第一個未被佔用的位置
      while (occupiedPositions.includes(position)) {
        position++;
      }
      
      // 更新該時間段需要的最大位置數
      maxPositionsNeeded = Math.max(maxPositionsNeeded, position + 1);
    }
    
    // 標記所有涵蓋的時間槽為已佔用
    for (let minute = startMinutes; minute < endMinutes; minute += 15) {
      const slotKey = Math.floor(minute / 15).toString();
      const occupiedPositions = timeSlots.get(slotKey) || [];
      occupiedPositions.push(position);
      timeSlots.set(slotKey, occupiedPositions);
    }
    
    // 將處理後的項目添加到結果中
    processed.push({
      ...item,
      position,
      totalPositions: maxPositionsNeeded,
      itemType: 'timeType' in item ? 'event' : 'timebox'
    });
  }
  
  // 回傳前再次計算每個項目的 totalPositions，確保一致性
  const finalProcessed = processed.map(item => {
    // 計算該項目時間範圍內的最大位置數
    const startMinutes = item.startTime.getHours() * 60 + item.startTime.getMinutes();
    const endMinutes = item.endTime 
      ? item.endTime.getHours() * 60 + item.endTime.getMinutes()
      : startMinutes + 60;
      
    let maxPositions = 1;
    for (let minute = startMinutes; minute < endMinutes; minute += 15) {
      const slotKey = Math.floor(minute / 15).toString();
      const occupiedPositions = timeSlots.get(slotKey) || [];
      maxPositions = Math.max(maxPositions, occupiedPositions.length);
    }
    
    return {
      ...item,
      totalPositions: maxPositions
    };
  });
  
  return finalProcessed;
};

// 格式化日期
// 注意：推薦使用 timeHelpers.ts 中的新時間處理函數
export const formatDate = (date: Date, format: string) => {
  return moment(date).format(format);
};

// 格式化時間範圍
// 注意：推薦使用 timeHelpers.ts 中的 formatTimeRange 函數，支援時區感知
export const formatTimeRange = (item: ScheduleItem): string => {
  if ('timeType' in item && item.timeType === 'allday') {
    return '全天';
  }
  
  const startTime = moment(item.startTime).format('HH:mm');
  
  if (!item.endTime) {
    return startTime;
  }
  
  const endTime = moment(item.endTime).format('HH:mm');
  return `${startTime} - ${endTime}`;
};

// 創建新的時間箱
export const createNewTimeBox = (start: Date, end: Date): TimeBox => {
  return {
    id: `temp-${Date.now()}`,
    itemType: 'timebox',
    userId: 'current-user',
    title: '',
    startTime: start,
    endTime: end,
    category: 'focus',
    createdAt: new Date(),
  };
};

// 創建新的 Event
export const createNewEvent = (start: Date, end?: Date, timeType: EventTimeType = 'timed'): Event => {
  let endTime: Date;

  if (end) {
    endTime = end;
  } else if (timeType === 'timed') {
    // 為 timed 事件提供預設結束時間（1小時後）
    endTime = new Date(start);
    endTime.setHours(start.getHours() + 1);
  } else {
    // 為 allday 事件設置合理的結束時間
    endTime = new Date(start);
    endTime.setHours(23, 59, 59, 999);
  }

  return {
    id: `temp-event-${Date.now()}`,
    itemType: 'event',
    userId: 'current-user',
    title: '',
    timeType,
    category: 'other',
    startTime: start,
    endTime: endTime,  // TypeScript 確保這裡 endTime 一定有值
    createdAt: new Date(),
  };
};

// 檢查項目是否為 Event  
export const isEvent = (item: ScheduleItem): item is Event => {
  return item.itemType === 'event';
};

// 檢查項目是否為 TimeBox
export const isTimeBox = (item: ScheduleItem): item is TimeBox => {
  return item.itemType === 'timebox';
};

// 獲取項目的顯示標題
export const getItemDisplayTitle = (item: ScheduleItem): string => {
  return item.title || (isEvent(item) ? '新事件' : '新時間箱');
};

// 獲取項目的樣式類名
export const getItemStyleClasses = (item: ScheduleItem): string => {
  if (isEvent(item)) {
    return 'bg-red-100 text-red-900 border-l-2 border-red-300';
  } else {
    // TimeBox 使用新的分類配置
    const timeBox = item as TimeBox;
    const categoryConfig = timeBoxCategoryConfig[timeBox.category];
    return categoryConfig ? categoryConfig.color : 'bg-slate-100 text-slate-900';
  }
};

// 獲取 Event 的統一樣式類名
export const getEventStyleClasses = (_event: Event): string => {
  // 統一使用紅色背景，強調外部承諾的重要性
  const baseClasses = 'bg-red-100 text-red-900 border border-red-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200';
  
  return baseClasses;
};

// 卡片間距常數（符合 Apple HIG 和 Material Design）
export const CARD_SPACING = {
  vertical: 3,    // 垂直間距 3px
  horizontal: 2,  // 水平間距 2px
  margin: 1,      // 邊距 1px
  borderRadius: 6, // 圓角 6px
};

// 獲取 TimeBox 的現代化樣式類名
export const getTimeBoxStyleClasses = (timeBox: TimeBox): string => {
  const categoryConfig = timeBoxCategoryConfig[timeBox.category];
  
  // 基礎樣式：保持現有的 TimeBox 樣式，增加 hover 和 transition 效果，邊框使用與視圖一致的灰色
  const baseClasses = 'rounded-md shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100';
  const categoryClasses = categoryConfig ? categoryConfig.color : 'bg-sky-100'; // 提供默認值以防萬一
  
  return `${baseClasses} ${categoryClasses}`;
};

// 開發環境下的邊界情況測試函數（已禁用 console 輸出）
export const testDateRangeBoundaries = () => {
  if (process.env.NODE_ENV !== 'development') return;
  
  // 測試週邊界 - 週日到週六的計算
  const testWeekBoundary = (testDate: Date) => {
    const startDate = new Date(testDate);
    const endDate = new Date(testDate);
    
    const dayOfWeek = startDate.getDay();
    startDate.setDate(startDate.getDate() - dayOfWeek);
    startDate.setHours(0, 0, 0, 0);
    endDate.setDate(endDate.getDate() - dayOfWeek + 6);
    endDate.setHours(23, 59, 59, 999);
    
    return { startDate, endDate, includesOriginal: testDate >= startDate && testDate <= endDate };
  };
  
  // 測試三天範圍
  const testThreeDayRange = (testDate: Date) => {
    const startDate = new Date(testDate);
    const endDate = new Date(testDate);
    
    endDate.setDate(endDate.getDate() + 2);
    endDate.setHours(23, 59, 59, 999);
    startDate.setHours(0, 0, 0, 0);
    
    return { startDate, endDate, includesOriginal: testDate >= startDate && testDate <= endDate };
  };
  
  // 測試當前日期（今天）
  const today = new Date();
  testWeekBoundary(today);
  testThreeDayRange(today);
  
  // 測試週日
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - today.getDay());
  testWeekBoundary(sunday);
  testThreeDayRange(sunday);
  
  // 測試週六
  const saturday = new Date(today);
  saturday.setDate(today.getDate() - today.getDay() + 6);
  testWeekBoundary(saturday);
  testThreeDayRange(saturday);
};

// 統一的日期範圍計算函數
export const calculateDateRange = (baseDate: Date, viewType: 'day' | 'threeDays' | 'week' | 'month') => {
  const startDate = new Date(baseDate);
  const endDate = new Date(baseDate);

  switch (viewType) {
    case 'day':
      // 單日：從當天開始到當天結束
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      break;

    case 'threeDays':
      // 三天視圖：載入 9 天資料（前 3 天 + 當前 3 天 + 後 3 天）
      // 這樣滑動時可以預覽其他區間的內容
      startDate.setDate(startDate.getDate() - 3);
      startDate.setHours(0, 0, 0, 0);
      endDate.setDate(endDate.getDate() + 5); // Day 0 + 5 = Day +5，共 9 天（Day -3 到 Day +5）
      endDate.setHours(23, 59, 59, 999);
      break;

    case 'week':
      // 週：計算週日到週六的完整範圍
      const dayOfWeek = startDate.getDay();
      startDate.setDate(startDate.getDate() - dayOfWeek);
      startDate.setHours(0, 0, 0, 0);
      endDate.setDate(endDate.getDate() - dayOfWeek + 6);
      endDate.setHours(23, 59, 59, 999);
      break;

    case 'month':
      // 月：從月初到月末
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);
      endDate.setMonth(endDate.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);
      break;

    default:
      throw new Error(`Unsupported view type: ${viewType}`);
  }

  return {
    startDate: new Date(startDate.getTime()),
    endDate: new Date(endDate.getTime())
  };
};

// 捲動配置常數
export const SCROLL_CONFIG = {
  INITIAL_DELAY: 300,
  SCROLL_BEHAVIOR: 'smooth' as const,
  VIEWPORT_OFFSET_RATIO: 0.25, // 25% from top
};

// 顯示配置常數
export const DISPLAY_CONFIG = {
  START_HOUR: 0,
  END_HOUR: 23,
  HOUR_HEIGHT: 90,
  TIME_COLUMN_WIDTH: 48,
  MIN_TIMEBOX_HEIGHT: 28,
};

// ========== 拖拽相關工具函數 ==========

// 拖拽操作類型
export type DragOperation = 'move' | 'resize-top' | 'resize-bottom';

// 拖拽狀態介面
export interface DragState {
  isDragging: boolean;
  operation: DragOperation;
  itemId: string;
  itemType: 'timebox' | 'event';
  startY: number;            // 開始拖動時的 Y 座標
  startX: number;            // 開始拖動時的 X 座標（用於日期切換）
  startMouseY: number;       // 鼠標在容器中的初始 Y 座標
  startMouseX: number;       // 鼠標在容器中的初始 X 座標
  originalStartTime: Date;
  originalEndTime: Date;
  currentStartTime: Date;
  currentEndTime: Date;
  originalDayIndex?: number; // 原始日期列索引（用於週視圖）
  currentDayIndex?: number;  // 當前日期列索引
}

// 時間對齊間隔（15 分鐘）
export const DRAG_CONFIG = {
  SNAP_INTERVAL_MINUTES: TIME_GRID_INTERVAL_MINUTES,  // 引用系統常量
  MIN_DURATION_MINUTES: 15,  // 最小持續時間
  RESIZE_HANDLE_HEIGHT: 8,   // 縮放手柄區域高度（像素）
};

/**
 * 將時間對齊到最近的 15 分鐘網格
 * @deprecated 建議直接使用 timePickerHelpers.snapTimeToGrid()
 * 保留此函數作為向後相容的別名
 */
export { snapTimeToGrid };

/**
 * 將像素偏移轉換為時間偏移
 * @param pixelOffset 像素偏移量
 * @param hourHeight 每小時的像素高度
 * @returns 時間偏移（分鐘）
 */
export const pixelsToMinutes = (pixelOffset: number, hourHeight: number): number => {
  return (pixelOffset / hourHeight) * 60;
};

/**
 * 將時間偏移轉換為像素偏移
 * @param minutes 時間偏移（分鐘）
 * @param hourHeight 每小時的像素高度
 * @returns 像素偏移量
 */
export const minutesToPixels = (minutes: number, hourHeight: number): number => {
  return (minutes / 60) * hourHeight;
};

/**
 * 根據拖動偏移計算新的時間
 * @param operation 拖拽操作類型
 * @param originalStartTime 原始開始時間
 * @param originalEndTime 原始結束時間
 * @param pixelDelta 像素偏移量
 * @param hourHeight 每小時的像素高度
 * @param startHour 日曆顯示的起始小時
 * @param endHour 日曆顯示的結束小時
 * @returns 新的開始和結束時間
 */
export const calculateNewTimes = (
  operation: DragOperation,
  originalStartTime: Date,
  originalEndTime: Date,
  pixelDelta: number,
  hourHeight: number,
  startHour: number = DISPLAY_CONFIG.START_HOUR,
  endHour: number = DISPLAY_CONFIG.END_HOUR
): { newStartTime: Date; newEndTime: Date } => {
  const minuteDelta = pixelsToMinutes(pixelDelta, hourHeight);
  const minDuration = DRAG_CONFIG.MIN_DURATION_MINUTES;

  let newStartTime = new Date(originalStartTime);
  let newEndTime = new Date(originalEndTime);

  switch (operation) {
    case 'move':
      // 移動：開始和結束時間同時偏移，保持持續時間不變
      newStartTime = new Date(originalStartTime.getTime() + minuteDelta * 60 * 1000);
      newEndTime = new Date(originalEndTime.getTime() + minuteDelta * 60 * 1000);
      break;

    case 'resize-top':
      // 從頂部調整：只改變開始時間
      newStartTime = new Date(originalStartTime.getTime() + minuteDelta * 60 * 1000);
      // 確保最小持續時間
      const maxStartTime = new Date(originalEndTime.getTime() - minDuration * 60 * 1000);
      if (newStartTime > maxStartTime) {
        newStartTime = maxStartTime;
      }
      break;

    case 'resize-bottom':
      // 從底部調整：只改變結束時間
      newEndTime = new Date(originalEndTime.getTime() + minuteDelta * 60 * 1000);
      // 確保最小持續時間
      const minEndTime = new Date(originalStartTime.getTime() + minDuration * 60 * 1000);
      if (newEndTime < minEndTime) {
        newEndTime = minEndTime;
      }
      break;
  }

  // 對齊到 15 分鐘網格
  newStartTime = snapTimeToGrid(newStartTime);
  newEndTime = snapTimeToGrid(newEndTime);

  // 邊界檢查：確保時間在顯示範圍內
  const dayStart = new Date(newStartTime);
  dayStart.setHours(startHour, 0, 0, 0);
  const dayEnd = new Date(newEndTime);
  dayEnd.setHours(endHour, 0, 0, 0);

  // 如果是移動操作，確保整個時間塊都在範圍內
  if (operation === 'move') {
    const duration = originalEndTime.getTime() - originalStartTime.getTime();

    if (newStartTime < dayStart) {
      newStartTime = new Date(dayStart);
      newEndTime = new Date(newStartTime.getTime() + duration);
    }

    if (newEndTime > dayEnd) {
      newEndTime = new Date(dayEnd);
      newStartTime = new Date(newEndTime.getTime() - duration);
    }
  } else {
    // 調整大小時的邊界檢查
    if (newStartTime < dayStart) {
      newStartTime = dayStart;
    }
    if (newEndTime > dayEnd) {
      newEndTime = dayEnd;
    }
  }

  return { newStartTime, newEndTime };
};

/**
 * 判斷鼠標位置對應的拖拽操作類型
 * @param mouseY 鼠標 Y 座標（相對於元素）
 * @param elementHeight 元素高度
 * @returns 拖拽操作類型
 */
export const getDragOperation = (mouseY: number, elementHeight: number): DragOperation => {
  const handleHeight = DRAG_CONFIG.RESIZE_HANDLE_HEIGHT;

  if (mouseY <= handleHeight) {
    return 'resize-top';
  } else if (mouseY >= elementHeight - handleHeight) {
    return 'resize-bottom';
  } else {
    return 'move';
  }
};

/**
 * 獲取拖拽游標樣式
 * @param operation 拖拽操作類型
 * @returns CSS 游標樣式
 */
export const getDragCursor = (operation: DragOperation): string => {
  switch (operation) {
    case 'resize-top':
    case 'resize-bottom':
      return 'ns-resize';
    case 'move':
      return 'grab';
    default:
      return 'default';
  }
};

/**
 * 根據滑鼠水平位置計算目標日期列索引
 * @param mouseX 滑鼠 X 座標（相對於容器）
 * @param containerWidth 容器寬度
 * @param timeColumnWidth 時間列寬度
 * @param columnCount 日期列數量
 * @returns 目標日期列索引 (0-based)
 */
export const calculateTargetDayIndex = (
  mouseX: number,
  containerWidth: number,
  timeColumnWidth: number,
  columnCount: number
): number => {
  // 計算日期區域的寬度（不包含時間列）
  const dateAreaWidth = containerWidth - timeColumnWidth;
  // 計算滑鼠在日期區域內的相對位置
  const relativeX = mouseX - timeColumnWidth;

  if (relativeX < 0) return 0;
  if (relativeX >= dateAreaWidth) return columnCount - 1;

  // 計算每列的寬度
  const columnWidth = dateAreaWidth / columnCount;
  // 計算目標列索引
  const targetIndex = Math.floor(relativeX / columnWidth);

  return Math.max(0, Math.min(columnCount - 1, targetIndex));
};

/**
 * 將時間調整到新的日期
 * @param originalTime 原始時間
 * @param targetDate 目標日期
 * @returns 調整後的時間（保留原時間的時分秒，日期改為目標日期）
 */
export const adjustTimeToNewDate = (originalTime: Date, targetDate: Date): Date => {
  const result = new Date(targetDate);
  result.setHours(
    originalTime.getHours(),
    originalTime.getMinutes(),
    originalTime.getSeconds(),
    originalTime.getMilliseconds()
  );
  return result;
};

/**
 * 根據拖動偏移計算新的時間（支援跨日期）
 * @param operation 拖拽操作類型
 * @param originalStartTime 原始開始時間
 * @param originalEndTime 原始結束時間
 * @param pixelDeltaY 垂直像素偏移量
 * @param hourHeight 每小時的像素高度
 * @param startHour 日曆顯示的起始小時
 * @param endHour 日曆顯示的結束小時
 * @param targetDate 目標日期（可選，用於跨日期拖動）
 * @returns 新的開始和結束時間
 */
export const calculateNewTimesWithDate = (
  operation: DragOperation,
  originalStartTime: Date,
  originalEndTime: Date,
  pixelDeltaY: number,
  hourHeight: number,
  startHour: number = DISPLAY_CONFIG.START_HOUR,
  endHour: number = DISPLAY_CONFIG.END_HOUR,
  targetDate?: Date
): { newStartTime: Date; newEndTime: Date } => {
  // 首先使用原有邏輯計算時間調整
  let { newStartTime, newEndTime } = calculateNewTimes(
    operation,
    originalStartTime,
    originalEndTime,
    pixelDeltaY,
    hourHeight,
    startHour,
    endHour
  );

  // 如果有目標日期且是移動操作，調整到新日期
  if (targetDate && operation === 'move') {
    const originalDate = new Date(originalStartTime);
    const targetDateOnly = new Date(targetDate);

    // 比較是否是不同日期
    if (
      originalDate.getFullYear() !== targetDateOnly.getFullYear() ||
      originalDate.getMonth() !== targetDateOnly.getMonth() ||
      originalDate.getDate() !== targetDateOnly.getDate()
    ) {
      // 計算時間偏移
      const startTimeOffset = newStartTime.getTime() - originalStartTime.getTime();
      const duration = originalEndTime.getTime() - originalStartTime.getTime();

      // 將開始時間調整到目標日期
      newStartTime = adjustTimeToNewDate(originalStartTime, targetDate);
      // 應用時間偏移
      newStartTime = new Date(newStartTime.getTime() + startTimeOffset);
      // 對齊到網格
      newStartTime = snapTimeToGrid(newStartTime);
      // 計算結束時間
      newEndTime = new Date(newStartTime.getTime() + duration);

      // 邊界檢查
      const dayStart = new Date(targetDate);
      dayStart.setHours(startHour, 0, 0, 0);
      const dayEnd = new Date(targetDate);
      dayEnd.setHours(endHour, 0, 0, 0);

      if (newStartTime < dayStart) {
        newStartTime = new Date(dayStart);
        newEndTime = new Date(newStartTime.getTime() + duration);
      }

      if (newEndTime > dayEnd) {
        newEndTime = new Date(dayEnd);
        newStartTime = new Date(newEndTime.getTime() - duration);
      }
    }
  }

  return { newStartTime, newEndTime };
};

// ========== 結束拖拽相關工具函數 ==========

// 滾動到當前時間的共用函數
export const scrollToCurrentTime = (
  scrollContainer: HTMLElement | null,
  startHour: number = DISPLAY_CONFIG.START_HOUR,
  hourHeight: number = DISPLAY_CONFIG.HOUR_HEIGHT
) => {
  if (!scrollContainer || scrollContainer.clientHeight === 0) {
    return;
  }

  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // 計算當前時間的位置（24 小時制，不需要範圍檢查）
  const scrollPosition = hourHeight * (currentHour - startHour) +
                        (currentMinute / 60) * hourHeight;

  // 讓當前時間位於視圖上方 25% 處
  const offset = scrollContainer.clientHeight * SCROLL_CONFIG.VIEWPORT_OFFSET_RATIO;
  const targetScrollTop = Math.max(0, scrollPosition - offset);

  scrollContainer.scrollTo({
    top: targetScrollTop,
    behavior: SCROLL_CONFIG.SCROLL_BEHAVIOR
  });
};